/**
 * React Query Client Configuration
 * 
 * Configures the QueryClient with default options for data fetching,
 * caching, and retry logic.
 * 
 * Features:
 * - Automatic retry with exponential backoff
 * - Configurable stale time and cache time
 * - Error handling integration
 * 
 * Requirements: 11.1, 11.2
 */

import { QueryClient } from '@tanstack/react-query';
import { isRetryableError } from '../utils/error-handler';

/**
 * Creates and configures the React Query client
 * 
 * Default configuration:
 * - Retry failed requests up to 3 times for retryable errors
 * - Cache data for 5 minutes
 * - Consider data stale after 1 minute
 * - Refetch on window focus for active queries
 * 
 * @returns Configured QueryClient instance
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // Retry configuration
                retry: (failureCount, error) => {
                    // Don't retry non-retryable errors (auth, not found, validation)
                    if (!isRetryableError(error)) {
                        return false;
                    }
                    // Retry up to 3 times for retryable errors
                    return failureCount < 3;
                },
                retryDelay: (attemptIndex) => {
                    // Exponential backoff: 1s, 2s, 4s
                    return Math.min(1000 * 2 ** attemptIndex, 30000);
                },

                // Cache configuration
                staleTime: 60 * 1000, // 1 minute - data is fresh for 1 minute
                gcTime: 5 * 60 * 1000, // 5 minutes - keep unused data in cache for 5 minutes

                // Refetch configuration
                refetchOnWindowFocus: true, // Refetch when user returns to tab
                refetchOnReconnect: true, // Refetch when network reconnects
                refetchOnMount: true, // Refetch when component mounts

                // Error handling
                throwOnError: false, // Don't throw errors, handle them in components
            },
            mutations: {
                // Retry configuration for mutations
                retry: (failureCount, error) => {
                    // Only retry network errors for mutations
                    if (!isRetryableError(error)) {
                        return false;
                    }
                    // Retry once for mutations
                    return failureCount < 1;
                },
                retryDelay: 1000, // 1 second delay for mutation retries
            },
        },
    });
}

/**
 * Query keys for consistent cache management.
 *
 * These keys are used throughout the application to identify
 * and invalidate cached data. Using a centralized key factory
 * ensures cache invalidation is predictable across hooks and mutations.
 *
 * @example
 * ```typescript
 * // Invalidate all job queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
 *
 * // Invalidate a specific job
 * queryClient.invalidateQueries({ queryKey: queryKeys.job('abc-123') });
 * ```
 */
export const queryKeys = {
    /** Base key for all job-related queries */
    jobs: ['jobs'] as const,

    /** Key for the filtered/paginated jobs list query */
    jobsList: (filters?: Record<string, unknown>) => ['jobs', 'list', filters] as const,

    /** Key for a single job detail query */
    job: (jobId: string) => ['jobs', 'detail', jobId] as const,

    /** Base key for all file-related queries */
    files: ['files'] as const,

    /** Key for a presigned file URL query */
    fileUrl: (key: string) => ['files', 'url', key] as const,

    /** Base key for all YouTube-related queries */
    youtube: ['youtube'] as const,

    /** Key for a YouTube upload status query */
    youtubeStatus: (jobId: string) => ['youtube', 'status', jobId] as const,

    /** Base key for all project-related queries */
    projects: ['projects'] as const,

    /** Key for the filtered/paginated projects list query */
    projectsList: (filters?: Record<string, unknown>) => ['projects', 'list', filters] as const,

    /** Key for a single project detail query */
    project: (projectId: string) => ['projects', 'detail', projectId] as const,

    /** Key for jobs within a project */
    projectJobs: (projectId: string, filters?: Record<string, unknown>) =>
        ['projects', 'jobs', projectId, filters] as const,

    /** Base key for all forum-related queries */
    forum: ['forum'] as const,

    /** Key for the filtered/sorted forum threads list query */
    forumThreads: (options?: Record<string, unknown>) => ['forum', 'threads', options] as const,

    /** Key for a single thread detail + replies query */
    threadDetail: (threadId: string) => ['forum', 'thread', threadId] as const,

    /** Base key for inbox queries */
    inbox: ['forum', 'inbox'] as const,

    /** Key for vote state on a thread */
    threadVote: (threadId: string) => ['forum', 'vote', threadId] as const,
} as const;
